import {
  bindSessionPort,
  RemoteTransport,
  decodeAgentIdentity,
  decodeProviderId,
  type RemoteDeviceSummary,
  type SyncStatus,
  type RemoteRecoveryIncident,
  type SessionDetail as SharedSessionDetail,
  type SessionSummary as SharedSessionSummary,
  type TerminalHistoryQuery as SharedTerminalHistoryQuery,
  type AgentOperationKind,
} from "@orni/sdk-typescript";
import type { CreateSessionInput, DesktopClient } from "@orni/domain-typescript";
import type { AutoContinuePolicy, Session, Snapshot } from "./domain";
import type {
  ApiCliId,
  ApiCliDescriptor,
  ApiCliBinding,
  ApiConfigApplyResult,
  ApiConfigPreview,
  ApiCurrentCliConfig,
  ApiImportCurrentConfigResult,
  ApiManagerSnapshot,
  ApiProviderRecord,
  ApiRequestLogPage,
  SaveApiCliSettingsInput,
  SaveApiProviderInput,
} from "./sdk/api-types";
import type {
  CreateNoteInput,
  NoteBlockActionInput,
  NoteBook,
  NoteBookDetail,
  NoteMutationResult,
  UpdateNoteInput,
} from "./sdk/note-types";
import type { RecoveryIncident, RecoveryIncidentAction } from "./sdk/recovery-types";
import type {
  ConfigurableProviderId,
  DeviceSettings,
  PermissionMode,
  ProviderHistoryCatalog,
  ProviderHistoryImportInput,
  ProviderHistorySourceInput,
  ProviderInventory,
  ProviderInventoryItem,
  TerminalConnection,
  TerminalHistoryQuery,
  TerminalSnapshot,
  UpdateProviderSettingsInput,
} from "./sdk/types";
import type { ProviderHistoryTargetOptions } from "@orni/sdk-typescript";
import { mapProviderHistoryCatalog } from "./sdk/local-contract";
import { NotesClient, type NoteBook as SharedNoteBook, type NoteBookDetail as SharedNoteDetail, type NoteMutationResult as SharedNoteMutation } from "@orni/sdk-typescript";
import {
  RemoteTerminalConnection,
  RemoteTerminalOperationJournal,
  NativeSessionTerminalCoordinator,
  NativeSessionTerminalCoordinatorPool,
} from "@orni/app-runtime";
import { normalizeSessionCreateInput } from "./session-create-input";
import { CloudApiManagerClient } from "./cloud-api-manager-client";
import { CloudEventsClient, type CloudEvent } from "@orni/sdk-typescript";
import { routeCloudEvent } from "./cloud-event-routing";

const providerNames = { codex: "Codex", claude: "Claude Code", opencode: "OpenCode" } as const;
function providerName(provider: SharedSessionSummary["provider"]): Session["provider"] {
  return providerNames[provider as keyof typeof providerNames] ?? "Codex";
}
// Computer-scoped discovery commands share the Daemon command inbox with
// session lifecycle work. Give a burst of settings/recovery/provider reads a
// bounded queueing window instead of turning a still-valid response into an
// empty Provider list after the generic 15s transport deadline.
const CLOUD_COMPUTER_STATE_TIMEOUT_MS = 45_000;
// Treat a stream as stable only after it survives a normal proxy idle window.
// Resetting backoff after a five-second handshake made a Cloudflare/Caddy
// reconnect loop hammer the page every second when the upstream was unhealthy.
const CLOUD_EVENT_STABLE_CONNECTION_MS = 30_000;
const emptyAutoContinue: AutoContinuePolicy = {
  enabled: true,
  recoveryEnabled: true,
  retryWindowSeconds: 180,
  retryForever: false,
  retryIntervalSeconds: 5,
  attemptsUsed: 0,
  recoveryHistory: [],
};

export interface CloudDesktopClientOptions {
  cloudOrigin?: string;
  targetDeviceId?: string;
  fetcher?: typeof fetch;
  recoverUnauthorized?: () => Promise<boolean>;
}

/**
 * Desktop's exact client contract backed by Cloud. This is intentionally an
 * adapter, not another page implementation: App and every session surface
 * continue to receive the same DesktopClient methods and data shape.
 */
export function createCloudDesktopClient(options: CloudDesktopClientOptions = {}): DesktopClient {
  const cloudOrigin = options.cloudOrigin?.trim().replace(/\/$/, "") ?? "";
  const fetcher = options.fetcher ?? globalThis.fetch.bind(globalThis);
  const remote = new RemoteTransport({
    cloudUrl: cloudOrigin,
    fetcher,
    recoverUnauthorized: options.recoverUnauthorized,
    client: { name: "web", version: "0.1.0", protocolVersion: 1 },
    commandReceiptStreamEnabled: true,
    commandReceiptTimeoutMs: CLOUD_COMPUTER_STATE_TIMEOUT_MS,
    preallocateSessionId: true,
    // Cloud Web uses the explicit Cloudflare PTY data plane. Local Desktop
    // sessions continue through RemoteDesktopSessions/LocalTransport.
    nativeTransport: "cloudflare",
  });
  const events = new CloudEventsClient({
    cloudUrl: cloudOrigin,
    fetcher,
    recoverUnauthorized: options.recoverUnauthorized,
    client: { name: "web", version: "0.1.0", protocolVersion: 1 },
  });
  const notes = new NotesClient({
    baseUrl: cloudOrigin,
    fetcher,
    recoverUnauthorized: options.recoverUnauthorized,
    wireFormat: "camelCase",
    client: { name: "web", version: "0.1.0", protocolVersion: 1 },
  });
  const apiManager = new CloudApiManagerClient((request) =>
    remote.requestApiManager(request));
  const listeners = new Set<(event: DesktopClientEvent) => void>();
  const terminalConnections = new Map<string, Promise<RemoteTerminalConnection>>();
  const terminalOperationJournals = new Map<string, RemoteTerminalOperationJournal>();
  // Cloud Web uses the same attach-first lifecycle coordinator as Mobile.
  // The legacy DesktopClient surface remains unchanged for the page layer.
  const terminalCoordinators = new NativeSessionTerminalCoordinatorPool<RemoteTerminalConnection>({ idleCloseMs: 3 * 60_000 });
  const terminalCoordinatorLeases = new Map<string, ReturnType<typeof terminalCoordinators.acquire>>();
  let targetDeviceId = options.targetDeviceId;
  let refreshTimer: ReturnType<typeof setInterval> | null = null;
  let eventReconnectTimer: ReturnType<typeof setTimeout> | null = null;
  let eventTask: Promise<void> | null = null;
  let eventAbort: AbortController | null = null;
  let eventCursor = 0;
  // Multiple SSE notifications can arrive while one durable event page is
  // being read. Keep the highest requested cursor so a later notification is
  // not lost when it races the in-flight page (the stream may close without
  // repeating that notification).
  let eventTargetCursor = 0;
  let eventCursorInitialized = false;
  let eventConsume: Promise<void> | null = null;
  let eventReconnectAttempt = 0;
  let disposed = false;
  let revision = 0;
  let lastSessionFingerprint = "";
  const optimisticLifecycle = new Map<string, { state: Session["state"]; expiresAt: number }>();

  const notify = () => {
    revision += 1;
    for (const listener of listeners) listener({ type: "snapshot.invalidated", revision });
  };
  const notifySessions = (sessionIds: string[], factKind: string) => {
    for (const sessionId of new Set(sessionIds.filter(Boolean))) {
      revision += 1;
      for (const listener of listeners) {
        listener({
          type: "session.detail.invalidated",
          revision,
          sessionId,
          factKind,
        });
      }
    }
  };
  const notifySpecialViews = () => {
    if (typeof window === "undefined") return;
    window.dispatchEvent(new Event("orni:notes-changed"));
    window.dispatchEvent(new Event("orni:api-changed"));
  };
  const notifyCloudEvent = (event: CloudEvent) => {
    // Events are invalidations, not a second data authority. The next App
    // refresh reads the fleet projection from Cloud; Notes pages use
    // the same browser event so they refresh without a manual reload.
    const invalidation = routeCloudEvent(event);
    if (invalidation.refreshNotes && typeof window !== "undefined") {
      window.dispatchEvent(new Event("orni:notes-changed"));
    }
    if (invalidation.refreshApiManager && typeof window !== "undefined") {
      window.dispatchEvent(new Event("orni:api-changed"));
    }
    notifySessions(invalidation.sessionIds, event.topic);
    if (invalidation.refreshSessionSnapshot) notify();
  };
  const consumeCloudEvents = async (latestCursor?: number): Promise<void> => {
    eventTargetCursor = Math.max(eventTargetCursor, latestCursor ?? 0);
    if (eventConsume) return eventConsume;
    eventConsume = (async () => {
      for (let pageCount = 0; pageCount < 20; pageCount += 1) {
        // A reconnect's `ready` notice and a changed notice can arrive while
        // this read is in flight. Capture the requested cursor and only move
        // the shared cursor forward; an older page must never move it back.
        const requestedAfter = eventCursor;
        const page = await events.list(requestedAfter, 100);
        if (page.gap) {
          const barrier = await events.snapshot();
          eventCursor = Math.max(eventCursor, barrier.cursor);
          // The barrier intentionally skips retained event payloads. Refresh
          // every specialized view so it re-reads its authoritative snapshot.
          notifySpecialViews();
          notify();
          return;
        }
        if (page.items.length) {
          eventCursor = Math.max(eventCursor, page.nextCursor);
          for (const event of page.items) notifyCloudEvent(event);
        }
        if (!page.items.length) {
          // PostgreSQL delivers NOTIFY after commit, but a Cloud node may
          // still be a few milliseconds behind the committing connection's
          // read snapshot. Retry a bounded number of times instead of losing
          // the invalidation until the reconciliation timer fires.
          if (latestCursor !== undefined && eventCursor < latestCursor && pageCount < 5) {
            await new Promise<void>((resolve) => setTimeout(resolve, 50 * (pageCount + 1)));
            continue;
          }
          return;
        }
        if (eventCursor >= eventTargetCursor) return;
      }
    })().finally(() => {
      eventConsume = null;
      // A newer notice may have arrived after the final page check but before
      // the promise settled. Schedule another catch-up without waiting for a
      // future SSE frame (or the five-second polling fallback).
      if (!disposed && eventCursor < eventTargetCursor) {
        void consumeCloudEvents(eventTargetCursor).catch(() => undefined);
      }
    });
    return eventConsume;
  };
  const waitForEventReconnect = async (): Promise<boolean> => {
    if (disposed || !listeners.size) return false;
    const delay = Math.min(30_000, 1_000 * (2 ** Math.min(eventReconnectAttempt, 5)));
    eventReconnectAttempt += 1;
    return await new Promise<boolean>((resolve) => {
      eventReconnectTimer = setTimeout(() => {
        eventReconnectTimer = null;
        resolve(!disposed && listeners.size > 0);
      }, delay);
    });
  };
  const runCloudEvents = async () => {
    while (!disposed && listeners.size) {
      if (!eventCursorInitialized) {
        try {
          // A newly opened browser tab has no durable local cursor. Replaying
          // every retained tenant event from zero does not make it more
          // correct: the App already reads authoritative Session/Note/API
          // snapshots, while thousands of historical invalidations make the
          // page repeatedly refetch those same snapshots. Establish a Cloud
          // read barrier first, then force one authoritative refresh after
          // the barrier before watching only newer events. Reconnects keep
          // the in-memory cursor and therefore still catch up incrementally.
          const barrier = await events.snapshot();
          eventCursor = barrier.cursor;
          eventCursorInitialized = true;
          notifySpecialViews();
          notify();
        } catch {
          if (!(await waitForEventReconnect())) return;
          continue;
        }
      }
      const controller = new AbortController();
      eventAbort = controller;
      const openedAt = Date.now();
      let streamReady = false;
      try {
        await events.watch({
          after: eventCursor,
          signal: controller.signal,
          onNotice: (notice) => {
            if (notice.type === "ready" || notice.type === "resync") {
              // `ready` is a read barrier, not permission to skip the durable
              // events between our cursor and the barrier. Catch them up
              // before advancing. Only `resync` (a retained-cursor gap) may
              // jump directly to the snapshot cursor.
              if (notice.type === "resync") {
                eventCursor = Math.max(eventCursor, notice.snapshot.cursor);
              } else if (eventCursor < notice.snapshot.cursor) {
                void consumeCloudEvents(notice.snapshot.cursor).catch(() => undefined);
              }
              streamReady = true;
              if (Date.now() - openedAt >= CLOUD_EVENT_STABLE_CONNECTION_MS) eventReconnectAttempt = 0;
              if (notice.type === "resync") notifySpecialViews();
              if (notice.type === "resync") notify();
              return;
            }
            if ("latestCursor" in notice) {
              // The signal is only a cursor barrier. Read the retained event
              // payloads first so the App invalidates the affected entity once
              // instead of performing a global refresh both before and after.
              void consumeCloudEvents(notice.latestCursor).catch(() => undefined);
            }
          },
        });
      } catch {
        if (controller.signal.aborted || disposed || !listeners.size) return;
      } finally {
        if (eventAbort === controller) eventAbort = null;
      }
      if (disposed || !listeners.size) return;
      // A healthy stream that is closed by a load balancer should not spin a
      // tight reconnect loop. Only a stream that stayed up long enough earns
      // a reset of the exponential backoff.
      if (streamReady && Date.now() - openedAt >= CLOUD_EVENT_STABLE_CONNECTION_MS) eventReconnectAttempt = 0;
      if (!(await waitForEventReconnect())) return;
    }
  };
  const startCloudEvents = () => {
    if (!eventTask) eventTask = runCloudEvents().finally(() => { eventTask = null; });
  };
  const chooseDevice = async (): Promise<string> => {
    if (targetDeviceId) return targetDeviceId;
    let devices = await remote.listDevices();
    // Device presence is cached briefly by RemoteTransport for fleet renders.
    // Creation is a state-changing path: if the cached snapshot has no online
    // computer, force one authoritative read so a computer that just renewed
    // its lease is not reported as unavailable for the cache TTL.
    if (!devices.some((device) => device.online)) {
      devices = await remote.listDevices({ refresh: true });
    }
    const selected = devices.find((device) => device.online) ?? devices[0];
    if (!selected) throw new Error("No enrolled computer is available. Connect a computer before creating a Session.");
    // Choosing a fallback target for one new Session must not turn the fleet
    // view into a permanent device filter. An explicit selector is the only
    // operation that changes the transport's selected computer.
    return selected.deviceId;
  };
  const listShared = async () => {
    const result = await remote.listSessions({ includeArchived: true, sort: "activity-desc" });
    revision = Math.max(revision, result.revision);
    lastSessionFingerprint = sessionFingerprint(result.sessions);
    return result;
  };
  const viewSummary = (source: SharedSessionSummary): Session => {
    const mapped = mapSummary(source);
    const pending = optimisticLifecycle.get(source.id);
    if (!pending) return mapped;
    if (Date.now() >= pending.expiresAt) {
      optimisticLifecycle.delete(source.id);
      return mapped;
    }
    return {
      ...mapped,
      state: pending.state,
      activity: pending.state === "starting" ? "Starting provider" : "Stopping provider",
      launchingWork: pending.state === "starting",
    };
  };
  const markLifecycle = (sessionId: string, state: Session["state"]) => {
    optimisticLifecycle.set(sessionId, { state, expiresAt: Date.now() + 20_000 });
    notifySessions([sessionId], "session.lifecycle.accepted");
  };
  const reconcileLifecycle = (sessionId: string) => {
    // Start watching only after Cloud accepted the command. RemoteTransport
    // captured the pre-command projection before dispatch, so this watcher
    // cannot mistake the command's own device lookup refresh for convergence.
    void remote.waitForSessionProjectionChange(sessionId, 20_000).then((changed) => {
      if (changed) optimisticLifecycle.delete(sessionId);
      notifySessions([sessionId], "session.lifecycle.updated");
      notify();
    }).catch(() => undefined);
  };

  const client: DesktopClient = {
    mode: "cloud",
    sessionPort: bindSessionPort(remote),
    terminalPort: {
      history: (sessionId: string, query?: SharedTerminalHistoryQuery) => getTerminalHistory(sessionId, query),
      connect: (sessionId: string) => connectTerminal(sessionId),
    },
    setRemoteSessionsEnabled() {
      // Cloud is already the active authority; authentication gates the app shell.
    },
    setCloudAccessEnabled() {
      // Cloud Web is already composed entirely against the authenticated Cloud.
    },
    listCloudDevices: (): Promise<RemoteDeviceSummary[]> => remote.listDevices(),
    setCloudTargetDevice(deviceId: string | null): void {
      targetDeviceId = deviceId?.trim() || undefined;
      remote.setTargetDevice(targetDeviceId ?? null);
      // API Manager is computer-scoped in Cloud Desktop. Tell an already
      // mounted API page to discard the previous computer's snapshot and
      // read the newly selected Daemon immediately.
      if (typeof window !== "undefined") window.dispatchEvent(new Event("orni:api-target-changed"));
      notify();
    },
    getCloudTargetDevice: (): string | null => targetDeviceId ?? remote.getTargetDevice(),
    getSyncStatus: (): Promise<SyncStatus> => remote.getSyncStatus(),
    listRecoveryIncidents: async () =>
      (await remote.listRecoveryIncidents(targetDeviceId)).map(mapRecoveryIncident),
    resolveRecoveryIncident: async (incidentId: string, action: RecoveryIncidentAction) =>
      mapRecoveryIncident(await remote.resolveRecoveryIncident(incidentId, action)),
    listNotes: async (sessionId?: string) => (await notes.listNotes(sessionId)).map(mapNoteBook),
    getNote: async (noteId, offset = 0, limit = 1_000) => mapNoteDetail(await notes.getNote(noteId, offset, limit)),
    createNote: async (input: CreateNoteInput = {}) => mapNoteDetail(await notes.createNote(input)),
    updateNote: async (noteId, input) => mapNoteDetail(await notes.updateNote(noteId, input)),
    deleteNote: (noteId, expectedRevision) => notes.deleteNote(noteId, expectedRevision),
    createNoteBlock: async (noteId, content, expectedNoteRevision) => mapNoteMutation(await notes.createBlock(noteId, { content, expectedNoteRevision })),
    createQuickNote: async (content) => {
      const inbox = (await notes.listNotes()).find((note) => note.isInbox);
      const note = inbox ?? (await notes.createNote({ isInbox: true })).note;
      return mapNoteMutation(await notes.createBlock(note.id, { content, expectedNoteRevision: note.revision }));
    },
    updateNoteBlock: async (blockId, content, expectedRevision) => mapNoteMutation(await notes.updateBlock(blockId, { content, expectedRevision })),
    deleteNoteBlock: async (blockId, expectedRevision) => mapNoteMutation(await notes.deleteBlock(blockId, expectedRevision)),
    reorderNoteBlocks: async (noteId, orderedBlockIds, expectedNoteRevision) => mapNoteMutation(await notes.reorderBlocks(noteId, orderedBlockIds, expectedNoteRevision)),
    actOnNoteBlock: async (blockId, input: NoteBlockActionInput) => mapNoteMutation(await notes.actOnBlock(blockId, input)),
    mergeNote: async (noteId, targetNoteId, expectedRevision) => mapNoteMutation(await notes.mergeNote(noteId, targetNoteId, expectedRevision)),
    snapshot: async (): Promise<Snapshot> => {
      const result = await listShared();
      const sessions = result.sessions.map((session) => viewSummary(session));
      return {
        sessions,
        attention: [],
        daemon: { state: "online", version: "Cloud", latency: 0 },
        preferences: { sessionRoot: "/" },
      };
    },
    subscribe(listener) {
      listeners.add(listener);
      startCloudEvents();
      if (!refreshTimer) {
        refreshTimer = setInterval(() => {
          if (disposed) return;
          void remote.listSessions({ includeArchived: true, sort: "activity-desc" })
            .then((result) => {
              const fingerprint = sessionFingerprint(result.sessions);
              if (fingerprint === lastSessionFingerprint) return;
              lastSessionFingerprint = fingerprint;
              revision = Math.max(revision, result.revision);
              notify();
            })
            .catch(() => undefined);
        }, 5_000);
      }
      return () => {
        listeners.delete(listener);
        if (!listeners.size && refreshTimer) {
          clearInterval(refreshTimer);
          refreshTimer = null;
        }
        if (!listeners.size) {
          eventAbort?.abort();
          if (eventReconnectTimer) clearTimeout(eventReconnectTimer);
          eventReconnectTimer = null;
        }
      };
    },
    getDeviceSettings: async (): Promise<DeviceSettings> => mapDeviceSettings(await remote.getDeviceSettings(targetDeviceId)),
    updateDesktopSettings: async (input) =>
      mapDeviceSettings(await remote.updateDeviceSettings(input, undefined, targetDeviceId)),
    completeOnboarding: async (input) => mapDeviceSettings(await remote.updateDeviceSettings({
      sessionRoot: input.sessionRoot,
      completeLegacyOnboarding: true,
      expectedRevision: input.expectedRevision,
    }, undefined, targetDeviceId)),
    discoverProviderHistory: async (sources?: ProviderHistorySourceInput[], provider?: ConfigurableProviderId, options?: Pick<ProviderHistoryTargetOptions, "cursor" | "cursorToken" | "limit" | "importStatus" | "refresh">): Promise<ProviderHistoryCatalog> => {
      const target = provider || options
        ? { ...(provider ? { provider } : {}), ...(options ?? {}) }
        : undefined;
      return mapProviderHistoryCatalog(await remote.discoverProviderHistory(sources ?? [], target));
    },
    importProviderHistory: (input: ProviderHistoryImportInput[]) =>
      remote.importProviderHistory(input),
    getProviderInventory: async () => mapProviderInventory(await remote.getProviderInventory(targetDeviceId)),
    refreshProviderInventory: async () => mapProviderInventory(await remote.refreshProviderInventory(targetDeviceId)),
    updateProviderSettings: async (providerId: ConfigurableProviderId, input: UpdateProviderSettingsInput) =>
      mapProviderInventory(await remote.updateProviderSettings(providerId, input, targetDeviceId)),
  agentOperation: (agentId: string, operation: AgentOperationKind, expectedRevision?: number) =>
    remote.agentOperation({ agentId, operation, expectedRevision }, targetDeviceId),
  waitForAgentOperation: async (commandId: string, operation: AgentOperationKind, options) => {
    const receipt = await remote.waitForAgentOperation(commandId, operation, options, targetDeviceId);
    return receipt;
  },
    // High-frequency read surfaces keep the dedicated read-only operations;
    // mutations and secret-bearing reads use the closed request allowlist.
    getApiManagerSnapshot: async () => mapApiManagerSnapshot(await remote.getApiManagerSnapshot()),
    saveApiProvider: (input: SaveApiProviderInput) => apiManager.saveProvider(input),
    deleteApiProvider: (providerId: string, expectedRevision?: number) => apiManager.deleteProvider(providerId, expectedRevision),
    getApiProviderCredential: (providerId: string) => apiManager.providerCredential(providerId),
    testApiProviderEndpoint: (providerId: string) => apiManager.testProviderEndpoint(providerId),
    testApiProviderModel: (providerId: string, model: string) =>
      apiManager.testProviderModel(providerId, model),
    fetchApiProviderModels: (providerId: string) => apiManager.fetchModels(providerId),
    detectApiProtocol: (input: { baseUrl: string; apiKey?: string }) =>
      apiManager.detectProtocol(input),
    updateApiProxy: (input: { enabled: boolean; port: number; expectedRevision?: number }) =>
      apiManager.updateProxy(input),
    saveApiCliSettings: (cli: ApiCliId, input: SaveApiCliSettingsInput) =>
      apiManager.saveCliSettings(cli, input),
    recoverApiCliBinding: (cli: ApiCliId, bindingId: string) =>
      apiManager.recoverCliBinding(cli, bindingId),
    getApiCurrentCliConfig: async (cli: ApiCliId): Promise<ApiCurrentCliConfig> =>
      mapApiCurrentCliConfig(
        await remote.readApiManager(`/v1/api-manager/cli/${encodeURIComponent(cli)}/current`),
        cli,
      ),
    importApiCurrentCliConfig: (cli: ApiCliId): Promise<ApiImportCurrentConfigResult> =>
      apiManager.importCurrentConfig(cli),
    previewApiCliConfig: (cli: ApiCliId): Promise<ApiConfigPreview> =>
      apiManager.configPreview(cli),
    applyApiCliConfig: (cli: ApiCliId, expectedSha256: string): Promise<ApiConfigApplyResult> =>
      apiManager.applyConfig(cli, expectedSha256),
    getApiRequestLogs: async (filters): Promise<ApiRequestLogPage> => {
      const query = new URLSearchParams();
      if (filters?.cli) query.set("cli", filters.cli);
      if (filters?.providerId) query.set("providerId", filters.providerId);
      if (filters?.protocolId) query.set("protocolId", filters.protocolId);
      return mapApiRequestLogPage(
        await remote.readApiManager(`/v1/api-manager/logs${query.size ? `?${query}` : ""}`),
      );
    },
    createSession: async (input: CreateSessionInput, onCreated) => {
      const deviceId = await chooseDevice();
      // The creation dialog always supplies the resolved workspace root. Do
      // not put another computer command in front of `sessions.create`; that
      // extra settings round trip made Cloud look idle even though the local
      // Daemon was ready. Keep the settings fallback for programmatic callers
      // that omit a workspace.
      const suppliedRoot = input.workspaceRoot?.trim();
      const sessionRoot = suppliedRoot
        || mapDeviceSettings(await remote.getDeviceSettings(deviceId)).sessionRoot;
      const normalized = normalizeSessionCreateInput(input, sessionRoot);
      if (!normalized.workspaceRoot) throw new Error("Choose a valid workspace.");
      const sessionTitle = input.title?.trim() || "Native Session";
      const detail = await remote.createSession({
        targetDeviceId: deviceId,
        title: sessionTitle || undefined,
        provider: providerId(input.provider),
        managementMode: "native",
        workspaceId: normalized.workspaceRoot,
        workspaceMode: normalized.workspaceMode,
        workspaceSubdirectory: normalized.workspaceSubdirectory,
        permissionMode: normalized.permissionMode,
        model: normalized.model,
        startImmediately: true,
      });
      const mapped = mapCloudSessionDetail(detail);
      // `sessions.create` queues creation and the initial start as one
      // remote operation. The Cloud projection may still expose the
      // pre-start `stopped` row for a short interval, so the client must use
      // one optimistic object for both the callback and the returned value.
      // Returning the raw projection here made App reconcile its pending
      // shell with `stopped` and render the inactive screen before the
      // target computer had a chance to publish the attempt.
      const starting = { ...mapped, state: "starting" as const, launchingWork: true };
      onCreated?.(starting);
      notify();
      return starting;
    },
    // Browser clients ask the selected computer to present its native folder
    // picker. The returned path belongs to that computer, not the browser.
    pickDirectoryPath: async () => remote.pickDirectoryPath(await chooseDevice()),
    getSession: async (sessionId) => viewSummary(await remote.getSession(sessionId)),
    getSessionWorkspace: async (sessionId) => remote.getSessionWorkspace(sessionId),
    renameSession: async (sessionId, title) => { await remote.renameSession(sessionId, title); notify(); },
    setSessionRoot: async (path) => { await remote.updateDeviceSettings(path, undefined, targetDeviceId); },
    startSession: async (sessionId) => {
      markLifecycle(sessionId, "starting");
      try {
        await remote.startSession(sessionId);
      } catch (error) {
        optimisticLifecycle.delete(sessionId);
        notifySessions([sessionId], "session.lifecycle.rejected");
        throw error;
      }
      reconcileLifecycle(sessionId);
      notify();
    },
    pauseSession: async (sessionId) => { await remote.pauseSession(sessionId); notify(); },
    recoverSession: async (sessionId, input = {}) => { await remote.recoverSession(sessionId, input); notify(); },
    stopSession: async (sessionId) => {
      markLifecycle(sessionId, "stopping");
      try {
        await remote.stopSession(sessionId);
      } catch (error) {
        optimisticLifecycle.delete(sessionId);
        notifySessions([sessionId], "session.lifecycle.rejected");
        throw error;
      }
      reconcileLifecycle(sessionId);
      notify();
    },
    stopAllSessions: async () => {
      const result = await listShared();
      const active = result.sessions.filter((session) => ["starting", "running", "waiting"].includes(session.status));
      await Promise.all(active.map((session) => remote.stopSession(session.id)));
      notify();
      return active.length;
    },
    restartSession: async (sessionId) => {
      markLifecycle(sessionId, "starting");
      try {
        await remote.restartSession(sessionId);
      } catch (error) {
        optimisticLifecycle.delete(sessionId);
        notifySessions([sessionId], "session.lifecycle.rejected");
        throw error;
      }
      reconcileLifecycle(sessionId);
      notify();
    },
    deleteSession: async (sessionId) => { await remote.deleteSession(sessionId); notify(); },
    connectTerminal,
    getTerminalHistory,
    requestTerminalControl: async (sessionId) => {
      const connection = await connectTerminal(sessionId);
      if (!connection) throw unsupported("This Session has no active remote terminal.");
      await connection.requestControl(false);
    },
    interruptSession: async (sessionId) => {
      const connection = await connectTerminal(sessionId);
      if (!connection) throw unsupported("This Session has no active remote terminal.");
      if (connection.controller.mode !== "owned") await connection.requestControl(false);
      connection.write(Uint8Array.of(0x1b));
    },
    setAutoContinue: async (sessionId, enabled) => {
      const current = await client.getSession(sessionId);
      await remote.setAutoContinuePolicy(sessionId, {
        ...current.autoContinuePolicy,
        enabled,
        recoveryEnabled: enabled,
      });
      notify();
    },
    setAutoContinuePolicy: async (sessionId, policy) => { await remote.setAutoContinuePolicy(sessionId, policy); notify(); },
    // SessionWorkspace uses the shared state method for its inline
    // "Start and connect" action. Route it through the lifecycle methods
    // above so Cloud gets the same optimistic state and projection watcher
    // as the Sessions list and restart actions.
    setSessionState: async (sessionId, state) => {
      if (state === "stopped") await client.stopSession(sessionId);
      else if (state === "running") await client.startSession(sessionId);
    },
    dispose() {
      disposed = true;
      if (refreshTimer) clearInterval(refreshTimer);
      refreshTimer = null;
      eventAbort?.abort();
      if (eventReconnectTimer) clearTimeout(eventReconnectTimer);
      eventReconnectTimer = null;
      for (const connection of terminalConnections.values()) {
        void connection.then((opened) => opened.close()).catch(() => undefined);
      }
      terminalConnections.clear();
      terminalCoordinators.closeAll();
      terminalCoordinatorLeases.clear();
      listeners.clear();
    },
  };
  return client;

  async function connectTerminal(sessionId: string): Promise<TerminalConnection | null> {
    let lifecycleLease = terminalCoordinatorLeases.get(sessionId);
    if (!lifecycleLease) {
      lifecycleLease = terminalCoordinators.acquire(sessionId, () => new NativeSessionTerminalCoordinator({
        sessionId,
        allowPendingAttach: remote.supportsPendingTerminalAttach,
        getSession: () => remote.getSession(sessionId),
        startSession: (operationId) => remote.startSession(sessionId, { operationId }),
        connect: (actual) => openRemoteConnection(actual.id),
        releaseConnection: (connection) => {
          connection.close();
          const pending = terminalConnections.get(sessionId);
          if (pending) {
            void pending.then((opened) => {
              if (opened === connection && terminalConnections.get(sessionId) === pending) {
                terminalConnections.delete(sessionId);
              }
            }).catch(() => undefined);
          }
        },
      }));
      terminalCoordinatorLeases.set(sessionId, lifecycleLease);
    }
    const coordinated = await lifecycleLease.coordinator.connect();
    if (coordinated.connection) return coordinated.connection;
    return null;
  }

  async function openRemoteConnection(sessionId: string): Promise<RemoteTerminalConnection> {
    const existing = terminalConnections.get(sessionId);
    if (existing) return existing;
    let connecting: Promise<RemoteTerminalConnection>;
    connecting = remote.openRemoteTerminal(sessionId).then((observer) =>
      new RemoteTerminalConnection(
        observer,
        (attachmentId, onEvent) =>
          remote.openRemoteTerminalControl(sessionId, attachmentId, onEvent),
        () => {
          if (terminalConnections.get(sessionId) === connecting) {
            terminalConnections.delete(sessionId);
          }
        },
        terminalOperationJournals.get(sessionId)
          ?? (() => {
            const journal = new RemoteTerminalOperationJournal();
            terminalOperationJournals.set(sessionId, journal);
            return journal;
          })(),
        () => remote.openRemoteTerminal(sessionId),
      ));
    terminalConnections.set(sessionId, connecting);
    try {
      return await connecting;
    } catch (cause) {
      if (terminalConnections.get(sessionId) === connecting) {
        terminalConnections.delete(sessionId);
      }
      throw cause;
    }
  }
  async function getTerminalHistory(sessionId: string, query: TerminalHistoryQuery = {}): Promise<TerminalSnapshot | null> {
    const history = await remote.getRemoteTerminalHistory(sessionId, query);
    const lines = history.frames.map((frame) => ({
      id: frame.id,
      sequence: frame.sequence,
      timestamp: frame.timestamp,
      kind: frame.kind === "snapshot" ? "output" as const : frame.kind,
      text: frame.text,
    }));
    return {
      sessionId,
      generation: history.generation ?? 0,
      firstSequence: lines[0]?.sequence ?? 0,
      lastSequence: lines.at(-1)?.sequence ?? 0,
      truncated: history.truncated ?? false,
      hasEarlier: history.hasEarlier ?? false,
      lines,
    };
  }
}

export function mapRecoveryIncident(source: RemoteRecoveryIncident): RecoveryIncident {
  return {
    incidentId: source.incidentId,
    deviceId: source.deviceId,
    deviceName: source.deviceName,
    sessionId: source.sessionId,
    processAttemptId: source.processAttemptId,
    previousGeneration: source.previousGeneration,
    sessionTitle: source.sessionTitle,
    providerId: source.providerId,
    workspaceId: source.workspaceId,
    managementMode: source.managementMode,
    restartCause: source.restartCause,
    classification: source.classification,
    confidence: source.confidence,
    evidence: source.evidence,
    ...(source.lastActivityAt ? { lastActivityAt: source.lastActivityAt } : {}),
    detectedAt: source.detectedAt,
    status: source.status,
    ...(source.resolvedAt ? { resolvedAt: source.resolvedAt } : {}),
    ...(source.detail ? { detail: source.detail } : {}),
  };
}

type DesktopClientEvent = Parameters<NonNullable<DesktopClient["subscribe"]>>[0] extends (event: infer E) => void ? E : never;

function providerId(provider: CreateSessionInput["provider"]): "codex" | "claude" | "opencode" {
  if (provider === "Codex") return "codex";
  if (provider === "Claude Code") return "claude";
  return "opencode";
}

function sessionFingerprint(sessions: SharedSessionSummary[]): string {
  return sessions
    .map((session) => `${session.id}:${session.status}:${session.lastActivityAt}:${session.preview}:${session.processAttemptId ?? ""}`)
    .join("|");
}

function mapSummary(source: SharedSessionSummary): Session {
  const state = mapState(source.status);
  const updatedAt = source.lastActivityAt || source.updatedAt;
  return {
    id: source.id,
    deviceId: source.ref.authority.kind === "device" ? source.ref.authority.deviceId : undefined,
    deviceName: source.deviceName,
    title: source.title,
    provider: providerName(source.provider),
    nativeTerminalOnly: source.tags.includes("origin:provider-history"),
    managementMode: "native",
    state,
    workspaceLabel: source.workspace.name || source.workspace.id,
    workspacePath: source.workspace.path,
    activity: source.preview || statusLabel(source.status),
    progress: source.taskProgress ?? (state === "running" ? 1 : 0),
    updated: relativeTime(updatedAt),
    updatedAt,
    autoContinue: source.autoContinue.enabled,
    processAttemptId: source.processAttemptId,
    generation: source.generation,
    autoContinuePolicy: mapAutoContinue(source.autoContinue),
    turnState: state === "running" ? "working" : state === "error" ? "failed" : "idle",
    controller: source.controller.mode === "owned" ? "you" : source.controller.mode === "held-elsewhere" ? "another device" : "none",
    leg: [{ id: source.id, provider: providerName(source.provider), state: "active", started: source.createdAt }],
  };
}

function mapCloudSessionDetail(source: SharedSessionDetail): Session {
  return mapSummary(source);
}

function mapState(status: SharedSessionSummary["status"]): Session["state"] {
  if (status === "archived") return "done";
  return status;
}

function mapAutoContinue(source: SharedSessionSummary["autoContinue"]): AutoContinuePolicy {
  return {
    enabled: source.enabled,
    recoveryEnabled: source.recoveryEnabled,
    retryWindowSeconds: source.retryWindowSeconds,
    retryForever: source.retryForever,
    retryIntervalSeconds: source.retryIntervalSeconds,
    attemptsUsed: source.attemptsUsed,
    recoveryStartedAt: source.recoveryStartedAt ?? undefined,
    lastRecoveryError: source.lastRecoveryError ?? undefined,
    recoveryHistory: source.recoveryHistory.map((record) => ({
      ...record,
      detail: record.detail ?? undefined,
      completedAt: record.completedAt ?? undefined,
    })),
  };
}

function statusLabel(status: SharedSessionSummary["status"]): string {
  return status === "running" ? "Working" : status === "starting" ? "Starting" : status === "waiting" ? "Waiting" : status;
}

function relativeTime(value: string): string {
  const timestamp = Date.parse(value);
  if (!Number.isFinite(timestamp)) return value;
  const seconds = Math.max(0, Math.floor((Date.now() - timestamp) / 1_000));
  if (seconds < 60) return "just now";
  if (seconds < 3_600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86_400) return `${Math.floor(seconds / 3_600)}h ago`;
  return `${Math.floor(seconds / 86_400)}d ago`;
}

export function mapNoteBook(source: SharedNoteBook): NoteBook {
  return { ...source, name: source.name ?? undefined, sessionId: source.sessionId ?? undefined, latestBlockFirstLine: source.latestBlockFirstLine ?? undefined };
}
export function mapNoteDetail(source: SharedNoteDetail): NoteBookDetail {
  return { note: mapNoteBook(source.note), blocks: source.blocks.map((block) => ({ ...block, mergeSourceNoteId: block.mergeSourceNoteId ?? undefined, mergeSourceTitle: block.mergeSourceTitle ?? undefined, mergeSourceBlockId: block.mergeSourceBlockId ?? undefined, mergedAt: block.mergedAt ?? undefined })), totalBlocks: source.totalBlocks };
}
export function mapNoteMutation(source: SharedNoteMutation): NoteMutationResult { return { note: mapNoteDetail(source.note), affectedBlockIds: source.affectedBlockIds }; }

export function mapProviderInventory(source: unknown): ProviderInventory {
  const root = source && typeof source === "object" && !Array.isArray(source)
    ? source as Record<string, unknown>
    : {};
  const rows = Array.isArray(root.providers) ? root.providers : [];
  const providers: ProviderInventoryItem[] = rows.flatMap((item) => {
    if (!item || typeof item !== "object" || Array.isArray(item)) return [];
    const row = item as Record<string, unknown>;
    const rawId = String(row.provider_id ?? row.providerId ?? row.agent_id ?? "");
    if (!rawId) return [];
    let identity;
    let projectedProviderId: ProviderInventoryItem["projectedProviderId"];
    try {
      identity = decodeAgentIdentity({ ...row, provider_id: rawId });
      projectedProviderId = decodeProviderId(rawId);
    } catch {
      return [];
    }
    const legacyProviderId = identity.legacyProviderId;
    const checkedAt = String(row.last_checked_at ?? row.lastCheckedAt ?? root.checked_at ?? root.checkedAt ?? new Date(0).toISOString());
    const nullable = (value: unknown): string | null => typeof value === "string" && value.length ? value : null;
    return [{
      providerId: legacyProviderId ?? "codex",
      projectedProviderId,
      agentId: identity.agentId,
      cliVariantId: identity.cliVariantId,
      legacyProviderId: identity.legacyProviderId,
      adapterId: typeof row.adapter_id === "string" ? row.adapter_id : typeof row.adapterId === "string" ? row.adapterId : null,
      adapterVersion: typeof row.adapter_version === "string" ? row.adapter_version : typeof row.adapterVersion === "string" ? row.adapterVersion : null,
      displayName: String(row.display_name ?? row.displayName ?? providerNames[rawId as keyof typeof providerNames] ?? rawId),
      installed: row.installed === true,
      enabled: row.enabled === true,
      supportsNative: legacyProviderId !== null && row.supports_native !== false && row.supportsNative !== false,
      supportsOrniWork: legacyProviderId !== null && (row.supports_orni_work === true || row.supportsOrniWork === true),
      terminalQuickCommands: (Array.isArray(row.terminal_quick_commands)
        ? row.terminal_quick_commands
        : Array.isArray(row.terminalQuickCommands) ? row.terminalQuickCommands : [])
        .flatMap((value) => {
          if (!value || typeof value !== "object" || Array.isArray(value)) return [];
          const command = String((value as Record<string, unknown>).command ?? "");
          if (command.length > 96 || !(/^(?:\/[a-z][a-z0-9-]*(?:[ \t]+[A-Za-z0-9._:\/@+-]+)*|!)$/.test(command))) return [];
          const rawSubmit = (value as Record<string, unknown>).auto_submit
            ?? (value as Record<string, unknown>).autoSubmit;
          const executionValue = (value as Record<string, unknown>).execution;
          const execution = executionValue === "draft" || executionValue === "submit" || executionValue === "interactive" || executionValue === "shell"
            ? executionValue
            : typeof rawSubmit === "boolean" ? (rawSubmit ? "submit" : "interactive") : command !== "/skills" ? "submit" : "interactive";
          const argumentHint = typeof (value as Record<string, unknown>).argument_hint === "string"
            ? (value as Record<string, unknown>).argument_hint as string
            : typeof (value as Record<string, unknown>).argumentHint === "string"
              ? (value as Record<string, unknown>).argumentHint as string
              : null;
          return [{
            command,
            execution,
            argumentHint,
            autoSubmit: typeof rawSubmit === "boolean" ? rawSubmit : execution === "submit",
          }];
        }),
      executablePath: nullable(row.executable_path ?? row.executablePath),
      configuredExecutablePath: nullable(row.configured_executable_path ?? row.configuredExecutablePath),
      version: nullable(row.version),
      homeDirectory: String(row.home_directory ?? row.homeDirectory ?? ""),
      configuredHomeDirectory: nullable(row.configured_home_directory ?? row.configuredHomeDirectory),
      apiBaseUrl: nullable(row.api_base_url ?? row.apiBaseUrl),
      apiKeyConfigured: row.api_key_configured === true || row.apiKeyConfigured === true,
      defaultModel: nullable(row.default_model ?? row.defaultModel),
      installCommand: String(row.install_command ?? row.installCommand ?? ""),
      upgradeCommand: String(row.upgrade_command ?? row.upgradeCommand ?? ""),
      lastCheckedAt: checkedAt,
      revision: typeof row.revision === "number" ? row.revision : 0,
    }];
  });
  return {
    providers,
    checkedAt: String(root.checked_at ?? root.checkedAt ?? new Date(0).toISOString()),
  };
}

export function mapDeviceSettings(source: unknown): DeviceSettings {
  const row = source && typeof source === "object" && !Array.isArray(source)
    ? source as Record<string, unknown>
    : {};
  return {
    sessionRoot: typeof row.session_root === "string" ? row.session_root : typeof row.sessionRoot === "string" ? row.sessionRoot : "/",
    remoteControlRoot: typeof row.remote_control_root === "string"
      ? row.remote_control_root
      : typeof row.remoteControlRoot === "string"
        ? row.remoteControlRoot
        : typeof row.session_root === "string"
          ? row.session_root
          : typeof row.sessionRoot === "string"
            ? row.sessionRoot
            : "/",
    defaultPermissionMode:
      row.default_permission_mode === "read-only" ||
      row.default_permission_mode === "workspace-write" ||
      row.default_permission_mode === "full-access"
        ? row.default_permission_mode
        : row.defaultPermissionMode === "read-only" ||
            row.defaultPermissionMode === "workspace-write" ||
            row.defaultPermissionMode === "full-access"
          ? row.defaultPermissionMode
          : "workspace-write",
    computerName: typeof row.computer_name === "string"
      ? row.computer_name
      : typeof row.computerName === "string"
        ? row.computerName
        : "",
    onboardingCompleted: row.onboarding_completed === true || row.onboardingCompleted === true,
    revision: typeof row.revision === "number" ? row.revision : 0,
    updatedAt: typeof row.updated_at === "string" ? row.updated_at : typeof row.updatedAt === "string" ? row.updatedAt : new Date(0).toISOString(),
  };
}

export function mapApiManagerSnapshot(source: unknown): ApiManagerSnapshot {
  const root = source && typeof source === "object" && !Array.isArray(source)
    ? source as Record<string, unknown>
    : {};
  const rows = (value: unknown): Record<string, unknown>[] =>
    Array.isArray(value)
      ? value.filter((item): item is Record<string, unknown> => Boolean(item && typeof item === "object" && !Array.isArray(item)))
      : [];
  const text = (value: unknown, fallback = "") => typeof value === "string" ? value : fallback;
  const optional = (value: unknown): string | undefined => typeof value === "string" && value.length ? value : undefined;
  const cliDescriptors = rows(root.cliDescriptors ?? root.cli_descriptors).flatMap((row) => {
    const cli = text(row.cli).trim();
    if (!cli) return [];
    const rawRoutes = row.proxyRoutes ?? row.proxy_routes;
    const proxyRoutes = Array.isArray(rawRoutes)
      ? rawRoutes.filter((route): route is string => typeof route === "string" && route.trim().length > 0)
      : [];
    return [{
      cli: cli as ApiCliId,
      displayName: text(row.displayName ?? row.display_name, cli),
      configSupported: row.configSupported === true || row.config_supported === true,
      proxyRoutes,
    } satisfies ApiCliDescriptor];
  });
  const protocols = rows(root.protocols).map((row) => ({
    protocolId: text(row.protocolId ?? row.protocol_id),
    family: text(row.family),
    authKind: text(row.authKind ?? row.auth_kind),
    ...(optional(row.defaultBaseUrl ?? row.default_base_url) ? { defaultBaseUrl: optional(row.defaultBaseUrl ?? row.default_base_url) } : {}),
    supportsModelListing: row.supportsModelListing === true || row.supports_model_listing === true,
    // Adapter identities are opaque at this boundary. Preserve a newly
    // advertised CLI instead of silently dropping it in an older client.
    cliSupport: rows(row.cliSupport ?? row.cli_support).flatMap((support) => {
      const cli = text(support.cli).trim();
      return cli ? [{ cli: cli as ApiCliId, direct: support.direct === true, proxy: support.proxy === true, noteCode: text(support.noteCode ?? support.note_code) }] : [];
    }),
  }));
  const providers = rows(root.providers).map((row) => ({
    providerId: text(row.providerId ?? row.provider_id),
    name: text(row.name),
    protocolId: text(row.protocolId ?? row.protocol_id),
    baseUrl: text(row.baseUrl ?? row.base_url),
    hasApiKey: row.hasApiKey === true || row.has_api_key === true,
    ...(optional(row.defaultModel ?? row.default_model) ? { defaultModel: optional(row.defaultModel ?? row.default_model) } : {}),
    ...(optional(row.homepageUrl ?? row.homepage_url) ? { homepageUrl: optional(row.homepageUrl ?? row.homepage_url) } : {}),
    ...(optional(row.notes) ? { notes: optional(row.notes) } : {}),
    metadata: row.metadata && typeof row.metadata === "object" && !Array.isArray(row.metadata) ? row.metadata as Record<string, string> : {},
    models: Array.isArray(row.models) ? row.models.filter((model): model is string => typeof model === "string") : [],
    lastTestStatus: (row.lastTestStatus === "available" || row.last_test_status === "available" ? "available" : row.lastTestStatus === "unavailable" || row.last_test_status === "unavailable" ? "unavailable" : "unknown") as ApiProviderRecord["lastTestStatus"],
    ...(optional(row.lastTestError ?? row.last_test_error) ? { lastTestError: optional(row.lastTestError ?? row.last_test_error) } : {}),
    ...(optional(row.lastTestAt ?? row.last_test_at) ? { lastTestAt: optional(row.lastTestAt ?? row.last_test_at) } : {}),
    revision: typeof row.revision === "number" ? row.revision : 0,
    createdAt: text(row.createdAt ?? row.created_at, new Date(0).toISOString()),
    updatedAt: text(row.updatedAt ?? row.updated_at, new Date(0).toISOString()),
  }));
  const proxyRow = root.proxy && typeof root.proxy === "object" && !Array.isArray(root.proxy) ? root.proxy as Record<string, unknown> : {};
  const endpoints = (value: unknown): Record<ApiCliId, { route: "responses" | "chat" | "anthropic"; url: string }[]> => {
    const result: Record<ApiCliId, { route: "responses" | "chat" | "anthropic"; url: string }[]> = {};
    const source = value && typeof value === "object" && !Array.isArray(value) ? value as Record<string, unknown> : {};
    for (const [cli, raw] of Object.entries(source)) {
      for (const row of rows(raw)) {
        const route = text(row.route);
        if (!(route === "responses" || route === "chat" || route === "anthropic")) continue;
        (result[cli] ??= []).push({ route, url: text(row.url) });
      }
    }
    return result;
  };
  const cliSettings = rows(root.cliSettings ?? root.cli_settings).flatMap((row) => {
    const cli = text(row.cli);
    if (!cli.trim()) return [];
    return [{
      cli: cli as ApiCliId,
      useProxy: row.useProxy === true || row.use_proxy === true,
      revision: typeof row.revision === "number" ? row.revision : 0,
      updatedAt: text(row.updatedAt ?? row.updated_at, new Date(0).toISOString()),
      configPath: text(row.configPath ?? row.config_path),
      bindings: rows(row.bindings).map((binding) => ({
        bindingId: text(binding.bindingId ?? binding.binding_id),
        cli: cli as ApiCliId,
        providerId: text(binding.providerId ?? binding.provider_id),
        priority: typeof binding.priority === "number" ? binding.priority : 0,
        enabled: binding.enabled === true,
        healthStatus: (["available", "degraded", "unavailable"].includes(text(binding.healthStatus ?? binding.health_status)) ? text(binding.healthStatus ?? binding.health_status) : "unknown") as ApiCliBinding["healthStatus"],
        consecutiveFailures: typeof binding.consecutiveFailures === "number" ? binding.consecutiveFailures : typeof binding.consecutive_failures === "number" ? binding.consecutive_failures : 0,
        ...(optional(binding.degradedSince ?? binding.degraded_since) ? { degradedSince: optional(binding.degradedSince ?? binding.degraded_since) } : {}),
        ...(optional(binding.lastAttemptAt ?? binding.last_attempt_at) ? { lastAttemptAt: optional(binding.lastAttemptAt ?? binding.last_attempt_at) } : {}),
        ...(optional(binding.lastSuccessAt ?? binding.last_success_at) ? { lastSuccessAt: optional(binding.lastSuccessAt ?? binding.last_success_at) } : {}),
        ...(optional(binding.lastFailureAt ?? binding.last_failure_at) ? { lastFailureAt: optional(binding.lastFailureAt ?? binding.last_failure_at) } : {}),
        ...(optional(binding.lastError ?? binding.last_error) ? { lastError: optional(binding.lastError ?? binding.last_error) } : {}),
        revision: typeof binding.revision === "number" ? binding.revision : 0,
      })),
    }];
  });
  return {
    daemonVersion: text(root.daemonVersion ?? root.daemon_version, "unknown"),
    ...(cliDescriptors.length ? { cliDescriptors } : {}),
    protocols,
    providers,
    proxy: {
      enabled: proxyRow.enabled === true,
      running: proxyRow.running === true,
      ...(optional(proxyRow.restoreError ?? proxyRow.restore_error) ? { restoreError: optional(proxyRow.restoreError ?? proxyRow.restore_error) } : {}),
      port: typeof proxyRow.port === "number" ? proxyRow.port : 0,
      revision: typeof proxyRow.revision === "number" ? proxyRow.revision : 0,
      updatedAt: text(proxyRow.updatedAt ?? proxyRow.updated_at, new Date(0).toISOString()),
      endpoints: endpoints(proxyRow.endpoints),
    },
    cliSettings,
  };
}

export function mapApiCurrentCliConfig(source: unknown, expectedCli: ApiCliId): ApiCurrentCliConfig {
  const row = source && typeof source === "object" && !Array.isArray(source)
    ? source as Record<string, unknown>
    : {};
  const text = (value: unknown, fallback = "") => typeof value === "string" ? value : fallback;
  const optional = (value: unknown): string | undefined => typeof value === "string" && value.length ? value : undefined;
  const status = text(row.status);
  return {
    cli: expectedCli,
    path: text(row.path),
    status: (status === "configured" || status === "unreadable" ? status : "not_configured") as ApiCurrentCliConfig["status"],
    ...(optional(row.providerName ?? row.provider_name) ? { providerName: optional(row.providerName ?? row.provider_name) } : {}),
    ...(optional(row.protocolId ?? row.protocol_id) ? { protocolId: optional(row.protocolId ?? row.protocol_id) } : {}),
    ...(optional(row.baseUrl ?? row.base_url) ? { baseUrl: optional(row.baseUrl ?? row.base_url) } : {}),
    ...(optional(row.defaultModel ?? row.default_model) ? { defaultModel: optional(row.defaultModel ?? row.default_model) } : {}),
    hasApiKey: row.hasApiKey === true || row.has_api_key === true,
    ...(optional(row.matchedProviderId ?? row.matched_provider_id) ? { matchedProviderId: optional(row.matchedProviderId ?? row.matched_provider_id) } : {}),
    usesOrniProxy: row.usesOrniProxy === true || row.uses_orni_proxy === true,
    ...(optional(row.error) ? { error: optional(row.error) } : {}),
    checkedAt: text(row.checkedAt ?? row.checked_at, new Date(0).toISOString()),
  };
}

export function mapApiRequestLogPage(source: unknown): ApiRequestLogPage {
  const root = source && typeof source === "object" && !Array.isArray(source)
    ? source as Record<string, unknown>
    : {};
  const rows = Array.isArray(root.logs)
    ? root.logs.filter((item): item is Record<string, unknown> => Boolean(item && typeof item === "object" && !Array.isArray(item)))
    : [];
  const text = (value: unknown, fallback = "") => typeof value === "string" ? value : fallback;
  const optional = (value: unknown): string | undefined => typeof value === "string" && value.length ? value : undefined;
  const cli = (value: unknown): ApiCliId | null => {
    const candidate = text(value).trim();
    return candidate ? candidate : null;
  };
  return {
    logs: rows.flatMap((row) => {
      const rowCli = cli(row.cli);
      if (!rowCli) return [];
      return [{
        logId: text(row.logId ?? row.log_id),
        cli: rowCli,
        ...(optional(row.providerId ?? row.provider_id) ? { providerId: optional(row.providerId ?? row.provider_id) } : {}),
        ...(optional(row.protocolId ?? row.protocol_id) ? { protocolId: optional(row.protocolId ?? row.protocol_id) } : {}),
        method: text(row.method),
        path: text(row.path),
        requestBytes: typeof row.requestBytes === "number" ? row.requestBytes : typeof row.request_bytes === "number" ? row.request_bytes : 0,
        ...(optional(row.userAgent ?? row.user_agent) ? { userAgent: optional(row.userAgent ?? row.user_agent) } : {}),
        ...(optional(row.model) ? { model: optional(row.model) } : {}),
        ...(typeof row.statusCode === "number" ? { statusCode: row.statusCode } : typeof row.status_code === "number" ? { statusCode: row.status_code } : {}),
        latencyMs: typeof row.latencyMs === "number" ? row.latencyMs : typeof row.latency_ms === "number" ? row.latency_ms : 0,
        outcome: text(row.outcome),
        ...(optional(row.error) ? { error: optional(row.error) } : {}),
        createdAt: text(row.createdAt ?? row.created_at, new Date(0).toISOString()),
      }];
    }),
    retainedMinutes: typeof root.retainedMinutes === "number" ? root.retainedMinutes : typeof root.retained_minutes === "number" ? root.retained_minutes : 15,
  };
}

function unsupported(message: string): Error {
  const error = new Error(message);
  error.name = "UnsupportedOperation";
  return error;
}
